"use client";

import { Suspense, useActionState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { loginAction } from "@/app/actions/auth";
import { GoogleButton } from "@/components/google-button";
import { BrandMark } from "@/components/brand";

const GOOGLE_ERRORS: Record<string, string> = {
  google_off: "Google sign-in is not available right now.",
  google_state: "Google sign-in expired or was interrupted. Please try again.",
  google_token: "Could not complete Google sign-in. Please try again.",
  google_profile: "Google did not return an email for your account.",
};

function Logo() {
  return (
    <Link href="/" className="flex items-center" aria-label="Clearline home">
      <BrandMark height={26} priority />
    </Link>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-[#080808]" />}>
      <LoginForm />
    </Suspense>
  );
}

function LoginForm() {
  const searchParams = useSearchParams();
  const next = searchParams.get("next") ?? "/";
  const err = searchParams.get("err") ?? undefined;
  const googleError = err ? GOOGLE_ERRORS[err] : undefined;

  const [state, formAction] = useActionState(loginAction, undefined);

  return (
    <div className="cl-scope">
      {/* The cl-* tokens and display/mono fonts from the Base44 build are not
          registered in this project's Tailwind theme, so we scope them here
          with the exact hex values from Base44's compiled CSS. */}
      <style>{authTokens}</style>

      <main className="min-h-screen bg-cl-bg flex flex-col items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <div className="flex justify-center mb-10">
            <Logo />
          </div>

          <h1 className="font-display text-3xl text-cl-text tracking-tight text-center mb-2">
            Welcome back
          </h1>
          <p className="text-cl-muted text-sm text-center mb-8">
            Sign in to access live cost pricing.
          </p>

          <div className="bg-cl-surface border border-cl-border rounded-2xl p-8">
            <GoogleButton next={next} />

            <div className="flex items-center gap-3 my-6">
              <div className="h-px flex-1 bg-cl-border" />
              <span className="text-[10px] font-mono text-cl-muted uppercase tracking-widest">
                or
              </span>
              <div className="h-px flex-1 bg-cl-border" />
            </div>

            <form action={formAction} className="space-y-6">
              <input type="hidden" name="next" value={next} />

              <div>
                <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                  className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base focus:border-cl-blue focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  placeholder="Your password"
                  required
                  autoComplete="current-password"
                  className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base focus:border-cl-blue focus:outline-none transition-colors"
                />
              </div>

              {googleError ? (
                <p className="text-sm text-red-400">{googleError}</p>
              ) : null}
              {state?.error ? (
                <p className="text-sm text-red-400">{state.error}</p>
              ) : null}

              <button
                type="submit"
                className="w-full bg-cl-blue text-cl-bg font-semibold text-sm py-3 rounded-full hover:bg-cl-blue/90 transition-colors"
              >
                Sign in
              </button>
            </form>
          </div>

          <p className="text-center text-sm text-cl-muted mt-8">
            New?{" "}
            <Link href="/signup" className="text-cl-blue hover:underline">
              Sign up
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}

// Scoped cl-* token definitions (exact hex from Base44's compiled CSS).
const authTokens = `
.cl-scope { font-family: Inter, ui-sans-serif, system-ui, sans-serif; }
.cl-scope .font-display { font-family: var(--font-space-grotesk), "Space Grotesk", sans-serif; }
.cl-scope .font-mono { font-family: ui-monospace, "SF Mono", monospace; }
.cl-scope.bg-cl-bg, .cl-scope .bg-cl-bg { background-color: #080808; }
.cl-scope .bg-cl-surface { background-color: #0e0e0e; }
.cl-scope .bg-cl-blue { background-color: #3b82f6; }
.cl-scope .hover\\:bg-cl-blue\\/90:hover { background-color: rgba(59, 130, 246, 0.9); }
.cl-scope .text-cl-text { color: #f0f0f0; }
.cl-scope .text-cl-muted { color: #888888; }
.cl-scope .text-cl-blue { color: #3b82f6; }
.cl-scope .text-cl-bg { color: #080808; }
.cl-scope .border-cl-border { border-color: #1c1c1c; }
.cl-scope .focus\\:border-cl-blue:focus { border-color: #3b82f6; }
.cl-scope input::placeholder { color: rgba(136, 136, 136, 0.35); }
`;
